import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Bookmark, Palette, BookOpen, Film, BookText, Loader2 } from 'lucide-react';
import { useAuth } from '../App';
import SaveButton from '../components/SaveButton';

const API = '/api';

const TABS = [
  { key: 'all', label: 'All', icon: Bookmark },
  { key: 'artwork', label: 'Artwork', icon: Palette },
  { key: 'series', label: 'Series', icon: BookOpen },
  { key: 'episode', label: 'Episodes', icon: Film },
  { key: 'novel', label: 'Novels', icon: BookText },
];

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return url;
  return `/${url}`;
};

const SavedItemCard = ({ item, targetType, targetId, navigate }) => {
  const data = item || {};

  const getLink = () => {
    switch (targetType) {
      case 'artwork': return `/art/${targetId}`;
      case 'series': return `/series/${targetId}`;
      case 'episode': return data.series_id ? `/series/${data.series_id}/read/${targetId}` : '#';
      case 'novel': return `/novel/${targetId}`;
      default: return '#';
    }
  };

  const getImage = () => {
    switch (targetType) {
      case 'artwork': return resolveImageUrl(data.image_url || data.images?.[0]?.url);
      case 'series': return resolveImageUrl(data.cover_url);
      case 'episode': return resolveImageUrl(data.cover_url || data.thumbnail_url);
      case 'novel': return resolveImageUrl(data.cover_url);
      default: return null;
    }
  };

  const getTitle = () => data.title || data.name || 'Untitled';
  const image = getImage();
  const TypeIcon = TABS.find((t) => t.key === targetType)?.icon || Bookmark;

  return (
    <Link
      to={getLink()}
      className="group block relative bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden hover:border-zinc-600 transition-colors"
      data-testid={`saved-item-${targetId}`}
    >
      <div className="aspect-[2/3] bg-zinc-800 overflow-hidden relative">
        {image ? (
          <img
            src={image}
            alt={getTitle()}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <TypeIcon className="w-10 h-10 text-zinc-700" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="p-2.5">
        <div className="flex items-start justify-between gap-1">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-white truncate group-hover:text-orange-400 transition-colors">
              {getTitle()}
            </h3>
            <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500 mt-0.5">
              <TypeIcon className="w-3 h-3" />
              {targetType.charAt(0).toUpperCase() + targetType.slice(1)}
            </span>
          </div>
          <div onClick={(e) => e.preventDefault()}>
            <SaveButton
              targetId={targetId}
              targetType={targetType}
              initialSaved={true}
              saveCount={data.save_count || 0}
              size="sm"
            />
          </div>
        </div>
      </div>
    </Link>
  );
};

const SavedLibrary = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  const fetchSaved = useCallback(
    async (reset = false) => {
      if (!user) return;
      const newSkip = reset ? 0 : skip;
      reset ? setLoading(true) : setLoadingMore(true);
      try {
        const token = localStorage.getItem('token');
        const typeParam = activeTab !== 'all' ? `&target_type=${activeTab}` : '';
        const res = await axios.get(
          `${API}/saves?skip=${newSkip}&limit=${limit}${typeParam}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (reset) {
          setItems(res.data.items);
          setSkip(limit);
        } else {
          setItems((prev) => [...prev, ...res.data.items]);
          setSkip(newSkip + limit);
        }
        setTotal(res.data.total);
      } catch {
        /* silent */
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [user, activeTab, skip],
  );

  useEffect(() => {
    fetchSaved(true);
  }, [activeTab, user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Bookmark className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400">Sign in to view your saved items</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-20 pb-12 px-4 sm:px-6 lg:px-8" data-testid="saved-library">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bookmark className="w-6 h-6 text-orange-400" />
            Saved
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{total} saved item{total !== 1 ? 's' : ''}</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-zinc-800 overflow-x-auto" data-testid="saved-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-orange-500 text-orange-400'
                  : 'border-transparent text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid={`saved-tab-${key}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Bookmark className="w-14 h-14 text-zinc-800 mb-4" />
            <h3 className="text-lg font-semibold text-zinc-400 mb-1">Nothing saved yet</h3>
            <p className="text-sm text-zinc-600">
              Browse content and tap the bookmark icon to save it here
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {items.map((entry) => (
                <SavedItemCard
                  key={entry.save_id}
                  item={entry.item}
                  targetType={entry.target_type}
                  targetId={entry.target_id}
                  navigate={navigate}
                />
              ))}
            </div>

            {/* Load more */}
            {items.length < total && (
              <div className="text-center mt-8">
                <button
                  onClick={() => fetchSaved(false)}
                  disabled={loadingMore}
                  className="px-6 py-2 text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded-full hover:border-zinc-500 transition-colors disabled:opacity-50"
                  data-testid="load-more-saved"
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SavedLibrary;
